import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import {
  useDailyHistoryPanelPageReset,
  useDailyHistoryPanelState,
} from '@/features/shared/hooks/useDailyHistoryPanelState';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { queryTests } from '@/features/tests/data/tests';
import {
  buildTestHistoryQueryInput,
  buildTestHistoryQueryStateKey,
} from '@/features/tests/pages/testHistoryQueries';
import { buildTestHistoryRows } from '@/features/tests/pages/testHistoryRows';
import { gridPaths, queryKeys } from '@/shared/hooks';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { TestRunStatus } from '@flowqa/shared';

type TestHistoryDateWindow = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
};

type UseTestHistoryPanelDataArgs = {
  panelDateQuickFilter: string;
  panelDateWindow: TestHistoryDateWindow;
  panelFilterStatuses: TestRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  panelFilterTests: string[];
  panelSearchValue: string;
  latestDateBounds: {
    startDay: string | null;
    endDay: string | null;
  };
  enabled?: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

type UseTestHistoryPanelDataResult = {
  dailyHistoryRows: ReturnType<typeof buildTestHistoryRows>;
  historyPage: number;
  historyPageSize: number;
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  dailyHistoryLoading: boolean;
  showErrorState: boolean;
  totalCount: number;
  setHistoryPage: (nextPage: number) => void;
  setHistoryPageSize: (nextPageSize: number) => void;
  setHistorySortDirection: (nextSortDirection: 'asc' | 'desc') => void;
  setHistorySortKey: (nextSortKey: DailyHistorySortKey) => void;
};

export const useTestHistoryPanelData = ({
  panelDateQuickFilter,
  panelDateWindow,
  panelFilterStatuses,
  panelFilterSuites,
  panelFilterTags,
  panelFilterTests,
  panelSearchValue,
  latestDateBounds,
  enabled = true,
  selectedOrganisationId,
  selectedProjectId,
}: UseTestHistoryPanelDataArgs): UseTestHistoryPanelDataResult => {
  const { resolvePreferFileNameOverDisplayName } = useProjectStatusPresentation();
  const preferFileNameOverDisplayName = resolvePreferFileNameOverDisplayName(selectedProjectId);
  const previousTotalCountRef = useRef(0);
  const {
    page: historyPage,
    pageSize: historyPageSize,
    sortKey: historySortKey,
    sortDirection: historySortDirection,
    isTransitionLoading,
    setPage: setHistoryPage,
    setPageSize: setHistoryPageSize,
    setSortKey: setHistorySortKey,
    setSortDirection: setHistorySortDirection,
  } = useDailyHistoryPanelState({
    defaultSortKey: 'name',
  });
  const effectiveStartDay =
    panelDateWindow.startDay ??
    (panelDateQuickFilter === 'latest' ? latestDateBounds.startDay : null);
  const effectiveEndDay =
    panelDateWindow.endDay ?? (panelDateQuickFilter === 'latest' ? latestDateBounds.endDay : null);
  const panelFilterStatusesKey = useMemo(
    () => [...panelFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterStatuses],
  );
  const panelFilterSuitesKey = useMemo(
    () => [...panelFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterSuites],
  );
  const panelFilterTagsKey = useMemo(
    () => [...panelFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterTags],
  );
  const panelFilterTestsKey = useMemo(
    () => [...panelFilterTests].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterTests],
  );

  const dailyHistoryQueryKey = useMemo(
    () =>
      buildTestHistoryQueryStateKey({
        panelDateQuickFilter,
        panelDateWindowEndDay: effectiveEndDay,
        panelDateWindowStartDay: effectiveStartDay,
        panelSearchValue,
        panelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        panelFilterTests,
        historySortDirection,
        historySortKey,
        historyPage,
        historyPageSize,
        selectedOrganisationId,
        selectedProjectId,
      }),
    [
      panelDateQuickFilter,
      effectiveEndDay,
      effectiveStartDay,
      panelSearchValue,
      panelFilterStatusesKey,
      panelFilterSuitesKey,
      panelFilterTagsKey,
      panelFilterTestsKey,
      historySortDirection,
      historySortKey,
      historyPage,
      historyPageSize,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  const dailyHistoryQuery = useQuery({
    queryKey: queryKeys.dataGrid(gridPaths.tests, 'daily-history', dailyHistoryQueryKey),
    queryFn: async () =>
      await queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...buildTestHistoryQueryInput({
          panelDateWindowEndDay: effectiveEndDay,
          panelDateWindowStartDay: effectiveStartDay,
          panelSearchValue,
          panelFilterStatuses,
          panelFilterSuites,
          panelFilterTags,
          panelFilterTests,
          historySortDirection,
          historySortKey,
          historyPage,
          historyPageSize,
        }),
      }),
    enabled,
  });

  useEffect(() => {
    if (dailyHistoryQuery.data?.total !== undefined) {
      previousTotalCountRef.current = dailyHistoryQuery.data.total;
    }
  }, [dailyHistoryQuery.data?.total]);

  const dailyHistoryRows = useMemo(
    () =>
      buildTestHistoryRows(dailyHistoryQuery.data?.items, panelFilterStatuses, {
        preferFileNameOverDisplayName,
      }),
    [dailyHistoryQuery.data?.items, panelFilterStatuses, preferFileNameOverDisplayName],
  );

  const pageResetKey = useMemo(
    () =>
      JSON.stringify({
        panelDateQuickFilter,
        effectiveEndDay,
        effectiveStartDay,
        panelFilterStatusesKey,
        panelFilterSuitesKey,
        panelFilterTagsKey,
        panelFilterTestsKey,
        panelSearchValue,
        selectedOrganisationId,
        selectedProjectId,
      }),
    [
      panelDateQuickFilter,
      effectiveEndDay,
      effectiveStartDay,
      panelFilterStatusesKey,
      panelFilterSuitesKey,
      panelFilterTagsKey,
      panelFilterTestsKey,
      panelSearchValue,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );
  useDailyHistoryPanelPageReset({ resetKey: pageResetKey, setPage: setHistoryPage });

  return {
    dailyHistoryRows,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    dailyHistoryLoading:
      isTransitionLoading || (dailyHistoryQuery.data === undefined && dailyHistoryQuery.isPending),
    showErrorState: dailyHistoryQuery.isError,
    totalCount: dailyHistoryQuery.data?.total ?? previousTotalCountRef.current,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
  };
};
